import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Loader2, MessageCircle, Code, FileSearch, FileText, Trash2, Sparkles } from 'lucide-react';
import { getAgentById } from '../data/agents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  MessageCircle,
  Code,
  FileSearch,
  FileText
};

const AgentChat = () => {
  const { agentId } = useParams();
  const agent = getAgentById(agentId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `${agentId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  if (!agent) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-mono mb-4">Agent not found</h1>
          <Link to="/solutions" className="text-[var(--accent-primary)] hover:underline">
            ← Back to Solutions
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[agent.icon];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/agent/chat`, {
        agent_id: agentId,
        session_id: sessionId,
        message: userMessage
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        error: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setInput(example);
    textareaRef.current?.focus();
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] pt-20 flex flex-col">
      {/* Header */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/solutions" 
                className="w-10 h-10 flex items-center justify-center border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-colors"
              >
                <ArrowLeft size={20} />
              </Link>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 flex items-center justify-center border"
                  style={{ borderColor: agent.color, backgroundColor: `${agent.color}15` }}
                >
                  <IconComponent size={20} style={{ color: agent.color }} />
                </div>
                <div>
                  <h1 className="font-mono font-semibold">{agent.shortName}</h1>
                  <p className="text-xs text-[var(--text-muted)]">AI-Powered Assistant</p>
                </div>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-muted)] hover:text-red-400 transition-colors"
              >
                <Trash2 size={16} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="container py-6 max-w-4xl mx-auto">
          {messages.length === 0 ? (
            // Welcome State
            <div className="text-center py-12">
              <div 
                className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2"
                style={{ borderColor: agent.color, backgroundColor: `${agent.color}10` }}
              >
                <IconComponent size={40} style={{ color: agent.color }} />
              </div>
              <h2 className="font-mono text-2xl font-semibold mb-3">{agent.name}</h2>
              <p className="text-[var(--text-secondary)] max-w-lg mx-auto mb-8">
                {agent.description}
              </p>
              
              {/* Example Prompts */}
              <div className="text-left max-w-2xl mx-auto">
                <p className="text-sm text-[var(--text-muted)] mb-3 flex items-center gap-2">
                  <Sparkles size={14} />
                  Try these examples:
                </p>
                <div className="grid gap-2">
                  {agent.examples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => handleExampleClick(example)}
                      className="text-left p-3 border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] transition-all text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    >
                      "{example}"
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Messages
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-4 ${
                      message.role === 'user'
                        ? 'bg-[var(--accent-primary)] text-white'
                        : message.error
                        ? 'bg-red-500/10 border border-red-500/30 text-red-500'
                        : 'bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-[var(--text-primary)]'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] p-4">
                    <div className="flex items-center gap-2 text-[var(--text-muted)]">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
        <div className="container py-4 max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder={agent.placeholder}
                rows={1}
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-subtle)] p-4 pr-12 text-[var(--text-primary)] focus:border-[var(--accent-primary)] focus:outline-none transition-colors resize-none"
                style={{ minHeight: '56px', maxHeight: '200px' }}
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed self-end"
              style={{ height: '56px' }}
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
            </button>
          </form>
          <p className="text-xs text-[var(--text-muted)] mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentChat;
